/*
 * A copy of the dispatch console's component of the same name.
 *
 * Its own header already says it: "when this console is joined by a second one, this moves to a
 * shared package". The second console is here and the move has not happened, because the move is to
 * a package and the constraint on packages is the one the ADR records. Nothing in it is specific to
 * either console — the geometry is `glyph()` from `01 Token System v2.dc.html`, which both work-
 * package files carry byte-identically, and the colours come from `@tdelivr/design-tokens`.
 *
 * A copy rather than a shared package: see `docs/adr/0021-two-consoles-and-no-shared-shell.md`.
 */
import type { CSSProperties, ReactNode } from "react";

import { classOfStatus, stateClass, type StateClassKey } from "@tdelivr/design-tokens";

/**
 * What state a row is in, drawn once for the whole console.
 *
 * The design's rule is that a state is never carried by colour alone: every badge pairs a glyph with
 * a word. Nine classes, nine distinct glyphs, and the word is the class's own label. So the same
 * information reaches an agent who cannot tell the two greens apart, an agent looking at a
 * monochrome print of a board, and an agent glancing at a screen from two metres away.
 *
 * Two classes are drawn solid rather than tinted — `human` and `failed` — and that is weight spent
 * deliberately: a row that needs a person is the reason somebody opened the board, and a terminal
 * failure is a peer of success rather than a footnote to it. The same order governs sorting, which is
 * why `sort` travels with the class rather than being decided per screen.
 *
 * The geometry comes from the design file's own `glyph()` function, value for value. The colours come
 * from the token custom properties, so a badge follows the theme without this component knowing which
 * theme it is in.
 *
 * When this console is joined by a second one, this moves to a shared package. It is here while there
 * is one consumer, because a package boundary drawn around a single caller is a guess.
 */

export interface StateBadgeProps {
  /**
   * The service that owns the lifecycle, which is not always the service being read.
   *
   * The demand queue reads DSP-05, a projection, and the five statuses on it belong to DSP-04's
   * dispatch saga. The lifecycle is the saga's, so DSP-04 is what gets consulted.
   */
  readonly code: string;
  /** The status value exactly as the contract publishes it. */
  readonly status: string;
  /**
   * The word to show, where the screen has a better one than the class's own label.
   *
   * The design's `badge()` takes exactly this and falls back to the class label, and the reason is
   * worth keeping: `finding_candidates` and `assigning` are both `active`, so a badge showing only
   * the class would tell an agent "In progress" for two states they would act on differently.
   * The glyph and the hue still come from the class, so the visual grammar holds either way.
   */
  readonly label?: string;
  /**
   * The class, where the screen knows it and a status-to-class table cannot.
   *
   * Two rules on the exceptions board need more than a status. A dispatch search two minutes from its
   * deadline is drawn as `failed` while its status still reads `finding_candidates`, because the thing
   * to act on is the clock. And a logistics exception's class comes from `severity` paired with
   * `status` — `open · critical` and `open · low` are not the same row, and LOG-11 has no lifecycle in
   * the shared table at all, so without this every logistics row would render unclassified.
   *
   * Given, not guessed: omitting it still consults the table, and an unknown status still says so.
   */
  readonly classKey?: StateClassKey;
  /**
   * Why the badge says what it says, where the screen has a reason and the design has no column for
   * it.
   *
   * **The one place this copy diverges from the dispatch console's, and it diverges because that one
   * has a bug.** Its offer monitor passes a `title` explaining why an offer whose stored row reads
   * `delivered` is drawn as `timed out`; its `StateBadge` destructures four props and renders a
   * `title` on the unclassified branch only, so that explanation never reaches the DOM. Both of this
   * console's screens need the same affordance for the same reason — each corrects a stored state
   * against a clock, and an administrator who notices has to be able to find out why.
   */
  readonly title?: string;
}

/**
 * The glyph, as the design draws it.
 *
 * Sizes are the design's multiples of a 10px base. Colour is `currentColor` rather than a value: the
 * pill sets its own text colour — white on the two solid classes, the hue's foreground on the other
 * seven — and the glyph is the same colour as the word beside it in every case.
 */
function glyphStyle(glyph: string): CSSProperties {
  const base: CSSProperties = {
    display: "inline-flex",
    flex: "none",
    boxSizing: "border-box",
    width: 10,
    height: 10,
  };
  switch (glyph) {
    case "ring":
      return { ...base, border: "2px solid currentColor", borderRadius: "50%" };
    case "ringdot":
      return {
        ...base,
        border: "2px solid currentColor",
        borderRadius: "50%",
        background: "radial-gradient(circle, currentColor 0 1.5px, transparent 2px)",
      };
    case "half":
      return {
        ...base,
        border: "2px solid currentColor",
        borderRadius: "50%",
        background: "linear-gradient(90deg, currentColor 50%, transparent 50%)",
      };
    case "dot":
      return { ...base, background: "currentColor", borderRadius: "50%" };
    case "diamond":
      return {
        ...base,
        width: 8,
        height: 8,
        margin: "0 1px",
        background: "currentColor",
        transform: "rotate(45deg)",
        borderRadius: 1,
      };
    case "triangle":
      return {
        ...base,
        width: 11,
        background: "currentColor",
        clipPath: "polygon(50% 0, 100% 100%, 0 100%)",
      };
    case "octagon":
      return {
        ...base,
        width: 11,
        height: 11,
        background: "currentColor",
        clipPath: "polygon(30% 0, 70% 0, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0 70%, 0 30%)",
      };
    case "unknown":
      // The only glyph that carries a character. Dashed and grey, because "not confirmed" borrows
      // neither success nor failure: neither is true yet.
      return {
        ...base,
        width: 12,
        height: 12,
        alignItems: "center",
        justifyContent: "center",
        border: "1.5px dashed currentColor",
        borderRadius: 3,
        fontSize: 8,
        fontWeight: 700,
        lineHeight: 1,
      };
    default:
      return { ...base, width: 9, height: 9, background: "currentColor", borderRadius: 2 };
  }
}

export function StateBadge({ code, status, label, classKey, title }: StateBadgeProps): ReactNode {
  const key = classKey ?? classOfStatus(code, status);

  if (key === undefined) {
    // Not a tenth class, and not a grey badge standing in for one. 68 services publish a status enum
    // the design's table does not cover, and a plausible-looking badge would hide that: an agent
    // would read a decided state where nobody has decided one. So the raw value is shown, marked as
    // unclassified, which is both honest to the agent and visible to whoever has to fix it.
    return (
      <span
        className="badge badge--unclassified"
        title={title ?? `${code} publishes ${status}; no state class`}
      >
        <span aria-hidden="true" className="badge__glyph" style={glyphStyle("unknown")}>
          ?
        </span>
        <code className="badge__raw">{label ?? status}</code>
      </span>
    );
  }

  const state = stateClass(key);
  return (
    <span
      className={`badge badge--${state.hue}${state.solid ? " badge--solid" : ""}`}
      data-state={key}
      {...(title === undefined ? {} : { title })}
    >
      <span aria-hidden="true" className="badge__glyph" style={glyphStyle(state.glyph)}>
        {state.glyph === "unknown" ? "?" : null}
      </span>
      {label ?? state.label}
    </span>
  );
}

/**
 * Board order: the class first, then whatever the caller orders within it.
 *
 * Exported beside the badge because the two belong together. A board that draws `human` first and
 * sorts it eighth has told the agent the state matters and then buried it.
 */
export function byStateClass(code: string, left: string, right: string): number {
  const rank = (status: string): number => {
    const key = classOfStatus(code, status);
    // An unclassified status sorts after every classified one rather than first. It is a gap in the
    // table, not an emergency on the board.
    return key === undefined ? Number.MAX_SAFE_INTEGER : stateClass(key).sort;
  };
  return rank(left) - rank(right);
}
